<template>
  <div>
    <!-- Mecánico: viewer-konva -->
    <ViewerPageKonva v-if="ready && _isMechanical"
      :canActionsPictogram="canActionsPictogram"
      :pictogram-id="pictogramId"
      :work-layer="workLayer"
      @epc-reload="() => { $emit('epc-reload') }"
      @epc-screenshot="(e) => { $emit('epc-screenshot', e) }"></ViewerPageKonva>

    <!-- No-mecánico (electrical / trenches): LayoutBuilder en modo progreso.
        El cliente solo registra avance, no edita el diseño. La escena viene
        del .epcb (no del DXF) — mucho más liviano para archivos grandes. -->

    <LayoutBuilder v-else-if="ready && pictogram"
      ref="layoutBuilder"
      :pictogram="pictogram"
      :entity-id="pictogram.project_id"
      :type-pictogram="pictogram.type"
      :can-actions-pictogram="canActionsPictogramClient"
      :work-layer="workLayer"
      viewer-mode="progress"
      @epc-reload="() => { $emit('epc-reload') }" />

    <!-- Loader inicial mientras se resuelve el tipo del pictograma -->
    <div v-else class="p-8 text-center">
      <b-icon pack="fas" icon="spinner" custom-class="fa-spin" size="is-large"></b-icon>
      <p class="mt-2 text-sm text-gray-500">{{ $t('loading') }}…</p>
    </div>
  </div>
</template>

<script>
import ViewerPageKonva from '../../components/pictograms/viewer-pixi/ViewerPageKonva.vue'
import LayoutBuilder from '../../components/pictograms/layout-builder/LayoutBuilder.vue'
import { getPictogram, getCurrentSceneBinaryUrl } from '../../components/pictograms/services/pictograms.service'
import { PICTOGRAM_TYPES } from '../../components/pictograms/constants/pictogram-types'
import { createLogger } from '../../components/pictograms/utils/logger'

const logger = createLogger('webviewer-dispatcher')

export default {
  middleware: "auth",
  title: "pictogram",

  components: {
    ViewerPageKonva,
    LayoutBuilder,
  },

  props: {

  },

  // data: () => ({
  data() {
    return {
      pictogramId:  null, // ID del pictograma (ej: 123)
      workLayer:    null, // // capa donde  trabajará el usuario, solo permite trabajar en esa capa si null trabaja en todas
      pictogram:   null,
      ready:       false,
      // Permisos: mecánico mantiene el comportamiento original (admin con
       // acceso completo). Cliente (no-mech) entra en modo solo-lectura — el
       // registro de avance se hace via el overlay interactivo del LayoutBuilder
       // en progress mode.
      canActionsPictogram: {
        save: true,
        select_entity: true,
        view_only: false,
      },
      canActionsPictogramClient: {
        save: false,
        select_entity: false,
        view_only: true,
      },
    }
  },
  computed: {
    _isMechanical() {
      return this.pictogram?.type === PICTOGRAM_TYPES.MECHANICAL
    },
  },

  async mounted() {
    this.workLayer = this.$route.params.wl // wl = worklayer
    this.pictogramId = parseInt(this.$route.params.pictogramId) ?? null;

    if (!this.pictogramId) return

    try {
      this.pictogram = await getPictogram(this.pictogramId)
    } catch (err) {
      logger.error('No se pudo cargar el pictograma:', err)
      this.$buefy?.toast?.open?.({
        message:  this.$t('error') || 'Error al cargar el pictograma',
        type:     'is-danger',
        position: 'is-top-right',
      })
      return
    }

    this.ready = true

    // No-mech: descargamos el .epcb y lo inyectamos al LayoutBuilder via
    // _LoadBinaryScene (Entrega B). Si el método aún no existe en el
    // LayoutBuilder queda warning en consola; el render sigue funcional
    // (LayoutBuilder muestra placeholder de carga).
    if (!this._isMechanical) {
      await this._DownloadBinaryScene()
    }
  },

  created () {

  },


  methods: {
    async _DownloadBinaryScene() {
      let res
      try {
        res = await getCurrentSceneBinaryUrl(this.pictogramId)
      } catch (errResp) {
        // Cuando axios rechaza (4xx/5xx) el interceptor manda directamente
        // `error.response` (no un Error de JS). Extraemos status + el código
        // que el backend devuelve en `data.error` para mostrar un mensaje
        // accionable según la causa.
        const status = errResp?.status ?? errResp?.response?.status ?? 0
        const code   = errResp?.data?.error ?? errResp?.response?.data?.error ?? 'unknown'

        logger.error('getCurrentSceneBinaryUrl failed:', { status, code, errResp })

        let msgKey
        if      (code === 'scene_binary_missing')  msgKey = 'scene_binary_not_uploaded_yet'
        else if (code === 'type_not_supported')    msgKey = 'scene_binary_type_not_supported'
        else if (code === 'signed_url_failed')     msgKey = 'scene_binary_signed_url_failed'
        else                                       msgKey = 'error_loading_scene'

        this.$buefy?.toast?.open?.({
          message:  this.$t(msgKey),
          type:     'is-danger',
          position: 'is-top-right',
        })
        return
      }

      // Backend respondió 200 — validamos que `url` venga poblada. Defensivo
      // contra el caso "disk local sin signed URL" donde temporaryUrl puede
      // devolver null sin lanzar excepción.
      if (!res?.url) {
        logger.error('getCurrentSceneBinaryUrl 200 sin url:', res)
        this.$buefy?.toast?.open?.({
          message:  this.$t('scene_binary_url_invalid'),
          type:     'is-danger',
          position: 'is-top-right',
        })
        return
      }

      try {
        // credentials:'omit' — la signed URL de S3 trae X-Amz-Signature y
        // axios incluye Bearer global → conflicto "Only one auth mechanism".
        const resp = await fetch(res.url, { method: 'GET', credentials: 'omit' })
        if (!resp.ok) throw new Error('scene_binary_download_failed')

        const buf = await resp.arrayBuffer()
        logger.info('binary scene downloaded:', buf.byteLength, 'bytes')

        // El LayoutBuilder se monta tras `ready=true`, pero el ref se enlaza
        // en el siguiente tick — esperamos antes de llamar a su API.
        await this.$nextTick()
        const lb = this.$refs.layoutBuilder
        if (lb && typeof lb._LoadBinaryScene === 'function') {
          lb._LoadBinaryScene(buf)
        } else {
          logger.warn('LayoutBuilder._LoadBinaryScene aún no implementado')
        }
      } catch (err) {
        logger.error('binary scene download/inject error:', err)
        this.$buefy?.toast?.open?.({
          message:  this.$t(err.message) || err.message || this.$t('error_loading_scene'),
          type:     'is-danger',
          position: 'is-top-right',
        })
      }
    },
  }
};
</script>
